import { useDebouncedCallback } from '@mantine/hooks'
import type { ReactNode } from 'react'
import { useCallback, useState } from 'react'

export type Suggestion<P = {}> = {
  id: string
  title: string
  subtitle?: string | null
  attributes?: [string, string | null | undefined][]
  imageUrl?: string | null
  renderIcon?: (props: { data: Suggestion<P> }) => ReactNode
  disabled?: boolean
} & P

export interface Suggest<P = {}> {
  (search: string, formData: FormData): Promise<Suggestion<P>[]>
}

export function useSuggestions<P = {}>(
  suggest?: Suggest<P>,
  debounceTimeout = 500,
): {
  isLoading: boolean
  suggestions: Suggestion<P>[]
  updateSuggestions: (
    search: string,
    formData?: FormData | HTMLFormElement | null,
  ) => void
  resetSuggestions: () => void
} {
  const [isLoading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<Suggestion<P>[]>([])
  const updateDebouncedSuggestions = useDebouncedCallback(
    async (search: string, formData?: FormData | HTMLFormElement | null) => {
      if (!suggest) return
      const suggestions = await suggest(
        search,
        formData instanceof HTMLFormElement
          ? new FormData(formData)
          : (formData ?? new FormData()),
      )
      setLoading(false)
      setSuggestions(suggestions)
    },
    debounceTimeout,
  )

  function updateSuggestions(
    search: string,
    formData?: FormData | HTMLFormElement | null,
  ) {
    setLoading(true)
    return updateDebouncedSuggestions(search, formData)
  }

  const resetSuggestions = useCallback(() => setSuggestions([]), [])

  return {
    isLoading,
    suggestions,
    updateSuggestions,
    resetSuggestions,
  }
}
